import type { QueryKey } from '@tanstack/react-query';

import { AsyncComboBoxBase } from './AsyncComboBoxBase';
import type { ComboBoxProps } from './ComboBox';
import { ComboBoxMode } from './types/ComboBoxMode';
import type { ComboBoxOption } from './types/ComboBoxOption';

interface AsyncComboBoxOptionsProps<Option extends ComboBoxOption> {
    optionsQueryKey: (search: string) => QueryKey;
    optionsQueryFn: (search: string) => Promise<Option[]>;
    customOptionsFilter?: (options: Option[], search: string) => Option[];
    prefetchOnHover?: boolean;
}

export type AsyncComboBoxProps<Option extends ComboBoxOption = ComboBoxOption> = Omit<
    ComboBoxProps<Option>,
    'options'
> &
    AsyncComboBoxOptionsProps<Option> & {
        selectedOptionQueryKey: (value: NonNullable<Option['value']>) => QueryKey;
        selectedOptionQueryFn: (value: NonNullable<Option['value']>) => Promise<Option | null>;
        showSelectedOptionAtTop?: boolean;
    };

export function AsyncComboBox<Option extends ComboBoxOption = ComboBoxOption>({
    value,
    optionsQueryKey,
    optionsQueryFn,
    customOptionsFilter,
    selectedOptionQueryKey,
    selectedOptionQueryFn,
    prefetchOnHover,
    showSelectedOptionAtTop,
    onChange,
    isClearable = false,
    ...props
}: AsyncComboBoxProps<Option>) {
    return (
        <AsyncComboBoxBase<Option>
            {...props}
            mode={ComboBoxMode.Single}
            value={value}
            optionsQueryKey={optionsQueryKey}
            optionsQueryFn={optionsQueryFn}
            selectedOptionQueryKey={selectedOptionQueryKey}
            selectedOptionQueryFn={selectedOptionQueryFn}
            customOptionsFilter={customOptionsFilter}
            isClearable={isClearable}
            prefetchOnHover={prefetchOnHover}
            showSelectedOptionAtTop={showSelectedOptionAtTop}
            onSelectionChange={(nextSelectedOptions, meta) => {
                onChange?.(nextSelectedOptions[0]?.value ?? null, meta);
            }}
        />
    );
}
